import { Box, Grid } from "@mui/material";
import React from "react";

import useCurrency from "../../../../../hooks/useCurrency";

import ExtendedSummaryCard from "./ExtendedSummaryCard";

const Summaries = ({
  productSalesList,
  productProfitabilityList,
  productSalesFreshness,
  isDataLoading,
}) => {
  const { convertCurrency, getCurrencySymbol } = useCurrency();
  const smoothingDays = 30;

  const getSmoothingDateWindow = () => {
    const baseDates = Object.values(productSalesList || {})[0]?.dates || [];
    return baseDates.slice(-smoothingDays);
  };

  const getDateStringIndex = (daysAgo) => {
    const now = new Date();
    now.setDate(now.getDate() - daysAgo);
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${month}-${day}`;
  };

  const getTotalProfit = (daysAgo) => {
    const value = getTotalDailyProfit(daysAgo);
    return (
      (value < 0 ? "-" : "") +
      getCurrencySymbol() +
      Number(convertCurrency(Math.abs(Math.round(value)))).toLocaleString()
    );
  };

  const profitPartPerUnit = (part) => {
    // Calculate smoothed per-unit amount over a fixed 30-day window
    if (
      productProfitabilityList === undefined ||
      productProfitabilityList === null ||
      Object.keys(productProfitabilityList).length === 0
    ) {
      return 0;
    } else {
      const smoothingDateWindow = getSmoothingDateWindow();
      if (smoothingDateWindow.length === 0) {
        return 0;
      }

      const totalProfitPart = Object.values(productProfitabilityList).reduce(
        (accAsin, currAsin) => {
          return (
            accAsin +
            smoothingDateWindow.reduce((accDay, dateKey) => {
              const currDay = currAsin?.[dateKey];
              return accDay + (currDay?.[part] || 0);
            }, 0)
          );
        },
        0
      );

      const windowSize = smoothingDateWindow.length;
      const totalUnitsSold = Object.values(productSalesList || {}).reduce(
        (accAsin1, currAsin1) =>
          accAsin1 +
          (currAsin1?.units || [])
            .slice(-windowSize)
            .reduce((accDay, currDay) => accDay + currDay, 0),
        0
      );
      // totalUnitsSold - is the profit value somehow ?????? BOAZ
      const profitPartPerUnit = totalProfitPart / totalUnitsSold;
      return profitPartPerUnit;
    }
  };

  const unitsSoldPerPeriod = (period) => {
    if (!productSalesList || Object.keys(productSalesList).length === 0) {
      return 0;
    }

    const dates = Object.values(productSalesList)[0]?.["dates"];
    if (!dates) return 0;

    let relevantIndexPeriod = [];

    if (typeof period === "string") {
      relevantIndexPeriod = [dates.indexOf(period)];
    } else if (typeof period === "object") {
      relevantIndexPeriod = period;
      if (period.length > 7) {
        relevantIndexPeriod.pop();
        relevantIndexPeriod.push(0);
      }
      relevantIndexPeriod = relevantIndexPeriod.map(
        (i) => dates.length - 1 - i
      );
    } else {
      relevantIndexPeriod = [dates.length - 1 - period];
    }

    return Object.values(productSalesList).reduce((accAsin, currAsin) => {
      return (
        accAsin +
        relevantIndexPeriod.reduce(
          (accDay, day) => accDay + (currAsin["units"][day] || 0),
          0
        )
      );
    }, 0);
  };

  // Mirrors the UnitEconomics/table formula: refund_recovery_rate * refund_rate * sales
  const getSmoothedDayRefundCost = (currDateString) => {
    if (!productProfitabilityList) return 0;
    return Object.values(productProfitabilityList).reduce((acc, asinData) => {
      const dayValues = asinData[currDateString];
      if (!dayValues) return acc;
      const refundRate = dayValues.refund_rate || 0;
      const refundRecoveryRate = dayValues.refund_recovery_rate ?? 1;
      return (
        acc +
        refundRecoveryRate * refundRate * (dayValues.orderedProductSales || 0)
      );
    }, 0);
  };

  const getTotalDailyProfit = (daysAgo) => {
    const currDateString = getDateStringIndex(daysAgo);
    if (productProfitabilityList) {
      const dayExtras =
        profitPartPerUnit("extra_fee") * unitsSoldPerPeriod(currDateString);
      const dayRefunds = getSmoothedDayRefundCost(currDateString);

      const otherProfitParts = Object.values(productProfitabilityList).reduce(
        (acc, curr) => {
          const dayValues = curr[currDateString];
          const sumDaily = dayValues
            ? dayValues["orderedProductSales"] +
              Object.entries(dayValues)
                .filter(
                  ([k, v]) =>
                    typeof v === "number" &&
                    k !== "asin" &&
                    k !== "orderedProductSales" &&
                    k !== "extra_fee" &&
                    k !== "refunded_amount" &&
                    k !== "returned_cogs" &&
                    k !== "units" &&
                    k !== "datePST" &&
                    k !== "refund_rate" &&
                    k !== "refund_recovery_rate" &&
                    // refund_units is a count of refunded units, not money.
                    k !== "refund_units" &&
                    // orderedProductSales is already net-of-VAT on the backend:
                    // the VAT line in the breakdown is the gross-to-net step,
                    // so subtracting vat_amount here would count it twice.
                    k !== "vat_amount"
                )
                .reduce((acc, [, val]) => acc + val * -1, 0)
            : 0;
          return acc + sumDaily;
        },
        0
      );
      return otherProfitParts - dayRefunds + dayExtras;
    } else {
      return 0;
    }
  };

  // Margin is over net-of-VAT sales (profitability's orderedProductSales), the
  // same basis the products table uses; the card's Sales figure stays gross.
  // For non-VAT marketplaces both are the same number.
  const getTotalMargin = (daysAgo) => {
    const sales = getTotalDailyProfitElement(daysAgo, "orderedProductSales");
    const profit = getTotalDailyProfit(daysAgo);
    return Math.round((profit / sales) * 100).toLocaleString() + "%" || 0;
  };

  const getTotalDailyProfitElement = (daysAgo, element) => {
    const currDateString = getDateStringIndex(daysAgo);
    return productProfitabilityList
      ? Object.values(productProfitabilityList).reduce((acc, curr) => {
          const dayValues = curr[currDateString];
          return acc + ((dayValues && dayValues[element]) || 0);
        }, 0)
      : 0;
  };

  const getTotalProfitElement = (daysAgo, element) => {
    const value = getTotalDailyProfitElement(daysAgo, element);
    if (element === "extra_fee" || element === "returned_cogs") {
      return (
        (value >= 0 ? "+" : "") +
        Number(convertCurrency(Math.round(value))).toLocaleString() +
        getCurrencySymbol()
      );
    } else {
      return (
        Number(convertCurrency(Math.round(value * -1))).toLocaleString() +
        getCurrencySymbol()
      );
    }
  };

  const getTotalDailyValue = (daysAgo, metric) => {
    return productSalesList
      ? Object.values(productSalesList).reduce(
          (acc, curr) =>
            acc +
            (metric === "deals"
              ? curr[metric] &&
                curr[metric][curr[metric].length - 1 - daysAgo] !== ""
                ? 1
                : 0
              : (curr[metric] &&
                  curr[metric][curr[metric].length - 1 - daysAgo]) ||
                0),
          0
        )
      : 0;
  };

  const getTotalString = (value, metric) => {
    if (metric === "units") {
      return Math.round(value).toLocaleString();
    } else if (metric === "sales") {
      return (
        getCurrencySymbol() +
        Number(convertCurrency(Math.round(value))).toLocaleString()
      );
    } else if (metric === "ppc") {
      return (
        "-" +
        getCurrencySymbol() +
        Number(convertCurrency(Math.round(value))).toLocaleString()
      );
    } else if (metric === "deals") {
      return value;
    } else {
      return 0;
    }
  };

  const getTotalDaily = (daysAgo, metric) => {
    const value = getTotalDailyValue(daysAgo, metric);
    return getTotalString(value, metric);
  };

  const getTotalForPeriod = (metric, days, includeLastDay = false) => {
    let totalMetric = 0;
    let lastDay = includeLastDay ? 0 : 1;
    days = includeLastDay ? days - 1 : days;
    for (let i = lastDay; i <= days; i++) {
      totalMetric += getTotalDailyValue(i, metric);
    }
    return getTotalString(totalMetric, metric);
  };

  const getTotalProfitForPeriod = (days, includeLastDay = false) => {
    let totalProfit = 0;
    let lastDay = includeLastDay ? 0 : 1;
    days = includeLastDay ? days - 1 : days;
    for (let i = lastDay; i <= days; i++) {
      totalProfit += getTotalDailyProfit(i);
    }
    return (
      Number(convertCurrency(Math.round(totalProfit))).toLocaleString() +
      getCurrencySymbol()
    );
  };

  const getTotalProfitElementForPeriod = (
    days,
    element,
    includeLastDay = false
  ) => {
    let totalElement = 0;
    let lastDay = includeLastDay ? 0 : 1;
    days = includeLastDay ? days - 1 : days;
    for (let i = lastDay; i <= days; i++) {
      totalElement += getTotalDailyProfitElement(i, element);
    }
    if (element === "extra_fee" || element === "returned_cogs") {
      return (
        (totalElement >= 0 ? "+" : "") +
        Number(convertCurrency(Math.round(totalElement))).toLocaleString() +
        getCurrencySymbol()
      );
    } else {
      return (
        Number(
          convertCurrency(Math.round(totalElement * -1))
        ).toLocaleString() + getCurrencySymbol()
      );
    }
  };

  const getTotalMarginForPeriod = (days, includeLastDay = false) => {
    let totalSales = 0;
    let totalProfit = 0;
    let lastDay = includeLastDay ? 0 : 1;
    days = includeLastDay ? days - 1 : days;
    for (let i = lastDay; i <= days; i++) {
      totalSales += getTotalDailyProfitElement(i, "orderedProductSales");
      totalProfit += getTotalDailyProfit(i);
    }
    return Math.round((totalProfit / totalSales) * 100).toLocaleString() + "%";
  };

  const getBreakdownVat = (period, func, includeLastDay) => {
    if (func !== getTotalProfitElementForPeriod) {
      return getTotalDailyProfitElement(period, "vat_amount");
    }
    let total = 0;
    const lastDay = includeLastDay ? 0 : 1;
    const days = includeLastDay ? period - 1 : period;
    for (let i = lastDay; i <= days; i++) {
      total += getTotalDailyProfitElement(i, "vat_amount");
    }
    return total;
  };

  // VAT is the first step from the gross Sales shown on the card down to
  // Profit. Hidden when there is none (non-VAT marketplaces).
  const getVatLineHTML = (vat, formatted) =>
    Math.round(vat) !== 0
      ? `
      <div class="fc" style="display: flex">
        <div style="width: 100%">VAT: </div>
        <div>${formatted}</div>
      </div>`
      : "";

  const getBreakdownHTMLstring = (period, func, includeLastDay) => {
    return `
      <style>
        @media (max-width: 850px) {
          .fc {
            flex-direction: column;
          }
        }
      </style>${getVatLineHTML(
        getBreakdownVat(period, func, includeLastDay),
        func(period, "vat_amount", includeLastDay)
      )}
      <div class="fc" style="display: flex">
        <div style="width: 100%">Cogs: </div>
        <div>${func(period, "cogs_cost", includeLastDay)}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">Refunds (Smoothed): </div>
        <div>-${getCurrencySymbol()}${(() => {
      if (func === getTotalProfitElementForPeriod) {
        let total = 0;
        let lastDay = includeLastDay ? 0 : 1;
        const days = includeLastDay ? period - 1 : period;
        for (let i = lastDay; i <= days; i++) {
          total += getSmoothedDayRefundCost(getDateStringIndex(i));
        }
        return Number(convertCurrency(Math.round(total))).toLocaleString();
      }
      return Number(
        convertCurrency(
          Math.round(getSmoothedDayRefundCost(getDateStringIndex(period)))
        )
      ).toLocaleString();
    })()}</div>
      </div>
      <div class="fc" style="display: flex;font-style: italic;color: lightgrey;font-size: 0.8em">
        <div style="width: 100%">Refunds (actual): </div>
        <div>${func(period, "refunded_amount", includeLastDay)}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">Promotions: </div>
        <div>${func(period, "item_promotion_discount", includeLastDay)}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">Fees: </div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">- Fulfillments: </div>
        <div>${func(period, "fulfillment_fee", includeLastDay)}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">- Referrals: </div>
        <div>${func(period, "referral_fee", includeLastDay)}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">- Extras (Smoothed): </div>
        <div>${getCurrencySymbol()}${Number(
      convertCurrency(
        Math.round(
          profitPartPerUnit("extra_fee") *
            unitsSoldPerPeriod(
              func === getTotalProfitElementForPeriod
                ? Array.from({ length: period }, (_, i) => i + 1)
                : period
            )
        )
      )
    ).toLocaleString()}</div>
      </div>
      <div class="fc" style="display: flex;font-style: italic;color: lightgrey;font-size: 0.8em">
        <div style="width: 100%">- Extras (actual): </div>
        <div>${func(period, "extra_fee", includeLastDay)}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">PPC: </div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">- Product: </div>
        <div>${func(period, "product_cost", includeLastDay)}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">- Brand: </div>
        <div>${func(period, "brand_cost", includeLastDay)}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">- Display: </div>
        <div>${func(period, "display_cost", includeLastDay)}</div>
      </div>
    `;
  };

  const getDaysPassedInMonth = () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const diffTime = Math.abs(today - startOfMonth);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Calculate last month's date range (days ago from today)
  const getLastMonthRange = () => {
    // Whole-day arithmetic only. `new Date()` carries the current time, so the
    // old `Math.ceil` on the distance to the 1st of last month rounded up to
    // one day too many (e.g. Jul 31 for "Last month (Aug)") whenever the loaded
    // range reached back before the 1st. Normalize every endpoint to local
    // midnight and round, so a DST 23h/25h day can't shift the window either.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Day 0 of the current month === last day of the previous month, at midnight
    const lastDayPrevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    const firstDayPrevMonth = new Date(
      lastDayPrevMonth.getFullYear(),
      lastDayPrevMonth.getMonth(),
      1
    );
    const dayMs = 1000 * 60 * 60 * 24;
    const startDaysAgo = Math.round((today - firstDayPrevMonth) / dayMs);
    const endDaysAgo = Math.round((today - lastDayPrevMonth) / dayMs);

    return { startDaysAgo, endDaysAgo };
  };

  // Get total value for last month
  const getTotalForLastMonth = (metric) => {
    const { startDaysAgo, endDaysAgo } = getLastMonthRange();
    let total = 0;
    for (let i = endDaysAgo; i <= startDaysAgo; i++) {
      total += getTotalDailyValue(i, metric);
    }
    return getTotalString(total, metric);
  };

  // Get actual daily profit (using actual refunds/extras, not smoothed)
  const getTotalDailyProfitActual = (daysAgo) => {
    const currDateString = getDateStringIndex(daysAgo);
    if (productProfitabilityList) {
      // Get actual extra_fee and refunded_amount for this day
      let dayExtras = 0;
      let dayRefunds = 0;

      // Calculate other profit parts (same as original, excluding extra_fee and refunded_amount)
      const otherProfitParts = Object.values(productProfitabilityList).reduce(
        (acc, curr) => {
          const dayValues = curr[currDateString];
          if (dayValues) {
            // Accumulate actual extras and refunds
            dayExtras += dayValues["extra_fee"] || 0;
            dayRefunds += dayValues["refunded_amount"] || 0;

            const sumDaily =
              dayValues["orderedProductSales"] +
              Object.entries(dayValues)
                .filter(
                  ([k, v]) =>
                    typeof v === "number" &&
                    k !== "asin" &&
                    k !== "orderedProductSales" &&
                    k !== "extra_fee" &&
                    k !== "refunded_amount" &&
                    k !== "returned_cogs" &&
                    k !== "units" &&
                    k !== "datePST" &&
                    k !== "refund_rate" &&
                    k !== "refund_recovery_rate" &&
                    // refund_units is a count of refunded units, not money.
                    k !== "refund_units" &&
                    // orderedProductSales is already net-of-VAT on the backend:
                    // the VAT line in the breakdown is the gross-to-net step,
                    // so subtracting vat_amount here would count it twice.
                    k !== "vat_amount"
                )
                .reduce((acc, [, val]) => acc + val * -1, 0);
            return acc + sumDaily;
          }
          return acc;
        },
        0
      );
      // Same formula as original: otherProfitParts - refunds + extras
      return otherProfitParts - dayRefunds + dayExtras;
    } else {
      return 0;
    }
  };

  // Get total profit for last month (using actual values, not smoothed)
  const getTotalProfitForLastMonth = () => {
    const { startDaysAgo, endDaysAgo } = getLastMonthRange();
    let total = 0;
    for (let i = endDaysAgo; i <= startDaysAgo; i++) {
      total += getTotalDailyProfitActual(i);
    }
    return (
      Number(convertCurrency(Math.round(total))).toLocaleString() +
      getCurrencySymbol()
    );
  };

  // Get total profit element for last month
  const getTotalProfitElementForLastMonth = (element) => {
    const { startDaysAgo, endDaysAgo } = getLastMonthRange();
    let total = 0;
    for (let i = endDaysAgo; i <= startDaysAgo; i++) {
      total += getTotalDailyProfitElement(i, element);
    }
    if (element === "extra_fee" || element === "returned_cogs") {
      return (
        (total >= 0 ? "+" : "") +
        Number(convertCurrency(Math.round(total))).toLocaleString() +
        getCurrencySymbol()
      );
    } else {
      return (
        Number(convertCurrency(Math.round(total * -1))).toLocaleString() +
        getCurrencySymbol()
      );
    }
  };

  // Get margin for last month (using actual values, not smoothed)
  const getTotalMarginForLastMonth = () => {
    const { startDaysAgo, endDaysAgo } = getLastMonthRange();
    let totalSales = 0;
    let totalProfit = 0;
    for (let i = endDaysAgo; i <= startDaysAgo; i++) {
      totalSales += getTotalDailyProfitElement(i, "orderedProductSales");
      totalProfit += getTotalDailyProfitActual(i);
    }
    return Math.round((totalProfit / totalSales) * 100).toLocaleString() + "%";
  };

  // Get breakdown HTML for last month (showing only actual values, no smoothing)
  const getBreakdownHTMLstringForLastMonth = () => {
    const { startDaysAgo, endDaysAgo } = getLastMonthRange();
    let vat = 0;
    for (let i = endDaysAgo; i <= startDaysAgo; i++) {
      vat += getTotalDailyProfitElement(i, "vat_amount");
    }
    return `
      <style>
        @media (max-width: 850px) {
          .fc {
            flex-direction: column;
          }
        }
      </style>${getVatLineHTML(
        vat,
        getTotalProfitElementForLastMonth("vat_amount")
      )}
      <div class="fc" style="display: flex">
        <div style="width: 100%">Cogs: </div>
        <div>${getTotalProfitElementForLastMonth("cogs_cost")}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">Refunds: </div>
        <div>${getTotalProfitElementForLastMonth("refunded_amount")}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">Promotions: </div>
        <div>${getTotalProfitElementForLastMonth(
          "item_promotion_discount"
        )}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">Fees: </div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">- Fulfillments: </div>
        <div>${getTotalProfitElementForLastMonth("fulfillment_fee")}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">- Referrals: </div>
        <div>${getTotalProfitElementForLastMonth("referral_fee")}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">- Extras: </div>
        <div>${getTotalProfitElementForLastMonth("extra_fee")}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">PPC: </div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">- Product: </div>
        <div>${getTotalProfitElementForLastMonth("product_cost")}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">- Brand: </div>
        <div>${getTotalProfitElementForLastMonth("brand_cost")}</div>
      </div>
      <div class="fc" style="display: flex">
        <div style="width: 100%">- Display: </div>
        <div>${getTotalProfitElementForLastMonth("display_cost")}</div>
      </div>
    `;
  };

  // Get last month's name (3-letter abbreviation)
  const getLastMonthName = () => {
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return lastMonth.toLocaleString("default", { month: "short" });
  };

  const daysPassedInMonth = getDaysPassedInMonth();
  const cardWidth = 4;

  const [displayBreakdown, setDisplayBreakdown] = React.useState(false);
  // const [refundsPerUnit] = React.useState(profitPartPerUnit("refunded_Amount"));
  // const [extrasPerUnit] = React.useState(profitPartPerUnit("extra_fee"));

  return (
    <Grid
      container
      spacing={1}
      style={{ "margin-bottom": "20px", "margin-top": "10px" }}
      justifyContent="center"
      alignItems="center"
    >
      <Box
        display="flex"
        overflow="none"
        whiteSpace="nowrap"
        p={2}
        style={{ width: "100%" }}
      >
        <Box
          display="flex"
          overflow="auto"
          whiteSpace="nowrap"
          p={2}
          style={{ width: "55%" }}
        >
          <Grid item xs={cardWidth}>
            <ExtendedSummaryCard
              dataTestId="today_summary_card"
              title="Today"
              salesFreshness={productSalesFreshness}
              valueUnits={getTotalDaily(0, "units")}
              valueProfit={getTotalProfit(0)}
              valueSales={getTotalDaily(0, "sales")}
              valuePPC={getTotalDaily(0, "ppc")}
              subTitleOne="Breakdown"
              totalValueOne={" "}
              showCollapseValueOne={true}
              valueOneExpended={getBreakdownHTMLstring(
                0,
                getTotalProfitElement
              )}
              subTitleTwo="Net margin"
              totalValueTwo={getTotalMargin(0)}
              isLoading={isDataLoading}
              setDisplayBreakdown={setDisplayBreakdown}
              displayBreakdown={displayBreakdown}
              hasDeals={getTotalDaily(0, "deals")}
            />
          </Grid>
          <Grid item xs={cardWidth} style={{ marginLeft: "5px" }}>
            <ExtendedSummaryCard
              dataTestId="yesterday_summary_card"
              title="Yesterday"
              salesFreshness={productSalesFreshness}
              valueUnits={getTotalDaily(1, "units")}
              valueProfit={getTotalProfit(1)}
              valueSales={getTotalDaily(1, "sales")}
              valuePPC={getTotalDaily(1, "ppc")}
              subTitleOne="Breakdown"
              totalValueOne={" "}
              showCollapseValueOne={true}
              valueOneExpended={getBreakdownHTMLstring(
                1,
                getTotalProfitElement
              )}
              subTitleTwo="Net margin"
              totalValueTwo={getTotalMargin(1)}
              isLoading={isDataLoading}
              setDisplayBreakdown={setDisplayBreakdown}
              displayBreakdown={displayBreakdown}
              hasDeals={getTotalDaily(1, "deals")}
            />
          </Grid>
          {[...Array(29).keys()].map((i) => (
            <Grid item xs={cardWidth} style={{ marginLeft: "5px" }} key={i + 2}>
              <ExtendedSummaryCard
                dataTestId={`${i + 2}_days_ago_summary_card`}
                title={`${i + 2} days ago`}
                valueUnits={getTotalDaily(i + 2, "units")}
                valueProfit={getTotalProfit(i + 2)}
                valueSales={getTotalDaily(i + 2, "sales")}
                valuePPC={getTotalDaily(i + 2, "ppc")}
                subTitleOne="Breakdown"
                totalValueOne={" "}
                showCollapseValueOne={true}
                valueOneExpended={getBreakdownHTMLstring(
                  i + 2,
                  getTotalProfitElement
                )}
                subTitleTwo="Net margin"
                totalValueTwo={getTotalMargin(i + 2)}
                isLoading={isDataLoading}
                setDisplayBreakdown={setDisplayBreakdown}
                displayBreakdown={displayBreakdown}
                hasDeals={getTotalDaily(i + 2, "deals")}
              />
            </Grid>
          ))}
        </Box>
        <Box
          display="flex"
          overflow="auto"
          whiteSpace="nowrap"
          p={2}
          style={{
            width: "45%",
            boxShadow: "-20px 0 20px -20px rgba(0,0,0,0.3)",
          }}
        >
          <Grid item style={{ minWidth: "45%", flexShrink: 1 }}>
            <ExtendedSummaryCard
              dataTestId="7_days_summary_card"
              title="7 days"
              valueUnits={getTotalForPeriod("units", 7)}
              valueProfit={getTotalProfitForPeriod(7)}
              valueSales={getTotalForPeriod("sales", 7)}
              valuePPC={getTotalForPeriod("ppc", 7)}
              subTitleOne="Breakdown"
              totalValueOne={" "}
              showCollapseValueOne={true}
              valueOneExpended={getBreakdownHTMLstring(
                7,
                getTotalProfitElementForPeriod,
                false
              )}
              subTitleTwo="Net margin"
              totalValueTwo={getTotalMarginForPeriod(7)}
              isLoading={isDataLoading}
              setDisplayBreakdown={setDisplayBreakdown}
              displayBreakdown={displayBreakdown}
            />
          </Grid>
          <Grid
            item
            style={{ marginLeft: "5px", minWidth: "45%", flexShrink: 1 }}
          >
            <ExtendedSummaryCard
              dataTestId="month_to_date_summary_card"
              title="Month to date"
              valueUnits={getTotalForPeriod("units", daysPassedInMonth, true)}
              valueProfit={getTotalProfitForPeriod(daysPassedInMonth, true)}
              valueSales={getTotalForPeriod("sales", daysPassedInMonth, true)}
              valuePPC={getTotalForPeriod("ppc", daysPassedInMonth, true)}
              subTitleOne="Breakdown"
              totalValueOne={" "}
              showCollapseValueOne={true}
              valueOneExpended={getBreakdownHTMLstring(
                daysPassedInMonth,
                getTotalProfitElementForPeriod,
                true
              )}
              subTitleTwo="Net margin"
              totalValueTwo={getTotalMarginForPeriod(daysPassedInMonth, true)}
              isLoading={isDataLoading}
              setDisplayBreakdown={setDisplayBreakdown}
              displayBreakdown={displayBreakdown}
            />
          </Grid>
          <Grid
            item
            style={{ marginLeft: "5px", minWidth: "50%", flexShrink: 1 }}
          >
            <ExtendedSummaryCard
              dataTestId="last_month_summary_card"
              title={`Last month (${getLastMonthName()})`}
              valueUnits={getTotalForLastMonth("units")}
              valueProfit={getTotalProfitForLastMonth()}
              valueSales={getTotalForLastMonth("sales")}
              valuePPC={getTotalForLastMonth("ppc")}
              subTitleOne="Breakdown"
              totalValueOne={" "}
              showCollapseValueOne={true}
              valueOneExpended={getBreakdownHTMLstringForLastMonth()}
              subTitleTwo="Net margin"
              totalValueTwo={getTotalMarginForLastMonth()}
              isLoading={isDataLoading}
              setDisplayBreakdown={setDisplayBreakdown}
              displayBreakdown={displayBreakdown}
            />
          </Grid>
        </Box>
      </Box>
    </Grid>
  );
};

export default Summaries;
